import { useCallback, useEffect, useState } from 'react';

/**
 * A boolean flag persisted to `localStorage` under `key`. Used for durable UI
 * preferences (e.g. the collapsed state of the NavRail) so a reload restores the
 * operator's chosen layout. SSR/no-storage safe: falls back to `defaultValue` and
 * never throws if `localStorage` is unavailable (private mode, tests without DOM).
 */
export function usePersistentBoolean(
  key: string,
  defaultValue: boolean,
): [boolean, (next: boolean) => void, () => void] {
  const [value, setValue] = useState<boolean>(() => read(key, defaultValue));

  // Keep in sync if another tab flips the same preference.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === key && e.newValue != null) setValue(e.newValue === 'true');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      write(key, next);
    },
    [key],
  );
  const toggle = useCallback(() => {
    setValue((prev) => {
      write(key, !prev);
      return !prev;
    });
  }, [key]);

  return [value, set, toggle];
}

function read(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? fallback : raw === 'true';
  } catch {
    return fallback;
  }
}

function write(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* storage unavailable — keep the in-memory value only */
  }
}
